import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Caption,
  Input,
  List,
  Placeholder,
  SegmentedControl,
  Section,
} from '@telegram-apps/telegram-ui';
import { BulkActionSheet } from '../components/BulkActionSheet';
import { ProductCard } from '../components/ProductCard';
import { ProductModal } from '../components/ProductModal';
import { ErrorState, LoadingState } from '../components/StateViews';
import { useDebounced } from '../hooks/useDebounced';
import { useProducts } from '../hooks/useProducts';
import { useMainButton } from '../lib/telegram';
import type { ProductStatus, ProductsListQuery } from '../types/product';

type StatusSegment = 'all' | ProductStatus;
type ProductModalMode = 'view' | 'links';

const SEGMENTS: { key: StatusSegment; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'draft', label: 'Черн.' },
  { key: 'active', label: 'Актив.' },
  { key: 'archived', label: 'Архив' },
];

export function ProductsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusSeg, setStatusSeg] = useState<StatusSegment>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<ProductModalMode>('view');
  const [selectedIds, setSelectedIds] = useState(() => new Set<string>());
  const [bulkSheetOpen, setBulkSheetOpen] = useState(false);

  const debouncedSearch = useDebounced(search, 300);
  const filters: ProductsListQuery = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      status: statusSeg === 'all' ? undefined : statusSeg,
      sort: 'sortOrder',
      order: 'asc',
      limit: 50,
    }),
    [debouncedSearch, statusSeg],
  );

  const q = useProducts(filters);

  const toggleId = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const items = q.data?.pages.flatMap((page) => page.items) ?? [];
  const bulkMode = selectedIds.size > 0;

  const openBulkSheet = useCallback(() => setBulkSheetOpen(true), []);
  const openParam = searchParams.get('open');

  const clearOpenParam = useCallback(() => {
    if (!searchParams.has('open')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openProduct = useCallback(
    (id: string) => {
      setSelectedMode('view');
      setSelectedId(id);
      clearOpenParam();
    },
    [clearOpenParam],
  );

  const openProductClientLinks = useCallback(
    (id: string) => {
      setSelectedMode('links');
      setSelectedId(id);
      clearOpenParam();
    },
    [clearOpenParam],
  );

  const closeProduct = useCallback(() => {
    setSelectedMode('view');
    setSelectedId(null);
    clearOpenParam();
  }, [clearOpenParam]);

  useEffect(() => {
    if (selectedIds.size === 0) setBulkSheetOpen(false);
  }, [selectedIds.size]);

  useEffect(() => {
    if (openParam) setSelectedId(openParam);
  }, [openParam]);

  useMainButton({
    text: `Действия (${selectedIds.size})`,
    onClick: openBulkSheet,
    visible: selectedIds.size > 0,
  });

  if (q.isPending && !q.data) return <LoadingState />;

  if (q.error) {
    const err =
      q.error instanceof Error ? q.error : new Error(String(q.error ?? 'Неизвестная ошибка'));
    return <ErrorState error={err} onRetry={() => void q.refetch()} />;
  }

  const count = items.length;
  const summary = `${count} товаров${q.hasNextPage ? '+' : ''}`;

  return (
    <>
      <List>
        <Section header="Каталог">
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <Input
                header="Поиск"
                placeholder="Название, SKU, категория…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={{ paddingTop: 22, display: 'flex', gap: 8 }}>
              <Button mode="filled" size="m" type="button" onClick={() => navigate('/products/new')}>
                + Добавить
              </Button>
              <Button mode="outline" size="m" type="button" onClick={() => navigate('/catalog/analyze')}>
                ✨ AI-каталогизация
              </Button>
            </div>
          </div>
          <div style={{ overflowX: 'auto', padding: '8px 0' }}>
            <SegmentedControl>
              {SEGMENTS.map((s) => (
                <SegmentedControl.Item
                  key={s.key}
                  selected={statusSeg === s.key}
                  onClick={() => setStatusSeg(s.key)}
                  type="button"
                >
                  {s.label}
                </SegmentedControl.Item>
              ))}
            </SegmentedControl>
          </div>
          <Caption level="2" style={{ marginTop: 4 }}>
            Найдено: {summary}
          </Caption>
          {bulkMode && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              <Button mode="outline" size="s" type="button" onClick={() => setSelectedIds(new Set())}>
                Снять выделение
              </Button>
              <Button
                mode="outline"
                size="s"
                type="button"
                onClick={() => setSelectedIds(new Set(items.map((i) => i.id)))}
              >
                Выбрать все на экране
              </Button>
            </div>
          )}
        </Section>

        {items.length === 0 ? (
          <Section header=" ">
            <Placeholder
              header="Пока пусто"
              description="Нажмите «+ Добавить», чтобы создать товар или проверьте фильтры."
            />
          </Section>
        ) : (
          <Section header=" ">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
              }}
            >
              {items.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  selected={selectedIds.has(p.id)}
                  bulkMode={bulkMode}
                  onToggleSelect={() => toggleId(p.id)}
                  onOpen={() => openProduct(p.id)}
                  onSendClient={() => openProductClientLinks(p.id)}
                />
              ))}
            </div>
            {q.hasNextPage && (
              <div style={{ marginTop: 12, padding: '0 8px' }}>
                <Button
                  mode="filled"
                  size="m"
                  stretched
                  loading={q.isFetchingNextPage}
                  onClick={() => void q.fetchNextPage()}
                  disabled={!q.hasNextPage || q.isFetchingNextPage}
                >
                  Загрузить ещё
                </Button>
              </div>
            )}
          </Section>
        )}
      </List>

      <BulkActionSheet
        open={bulkSheetOpen}
        ids={[...selectedIds]}
        onClose={() => setBulkSheetOpen(false)}
        onCompleted={() => setSelectedIds(new Set())}
      />

      <ProductModal
        open={selectedId !== null}
        productId={selectedId}
        initialMode={selectedMode}
        onClose={closeProduct}
      />
    </>
  );
}
